import { FC, useState } from 'react'
import { Skeleton } from 'antd'
import { globalToaster } from 'components/elements/global'
import {
 useAddDomainRecordMutation,
 useDeleteDomainRecordMutation,
 useGetDomainRecordsQuery,
 useUpdateDomainRecordMutation,
} from '../api'
import { TDnsRecord } from '../types'
import { getErrorMessage, toRecordBody } from '../utils'
import RecordsTable, { LockTag } from './RecordsTable'
import RecordModal from './RecordModal'
import DeleteRecordModal from './DeleteRecordModal'

// Live records on a connected domain (rendered inside DnsRecordsModal): every change calls the API right away
// and the table only updates after a successful response (no optimistic UI)
const DnsRecordsSection: FC<{ domainId: number }> = ({ domainId }) => {
 const {
  data: records,
  isLoading,
  isError,
  error,
  refetch,
 } = useGetDomainRecordsQuery(domainId, { refetchOnMountOrArgChange: true })
 const [addRecord, { isLoading: isAdding }] = useAddDomainRecordMutation()
 const [updateRecord, { isLoading: isUpdating }] =
  useUpdateDomainRecordMutation()
 const [deleteRecord, { isLoading: isDeleting }] =
  useDeleteDomainRecordMutation()

 // null = closed; record null = add, otherwise edit
 const [editing, setEditing] = useState<{ record: TDnsRecord | null } | null>(
  null,
 )
 const [deleting, setDeleting] = useState<TDnsRecord | null>(null)

 const onSave = async (record: TDnsRecord): Promise<void> => {
  try {
   if (editing?.record?.id != null) {
    await updateRecord({
     id: domainId,
     recordId: editing.record.id,
     body: toRecordBody(record),
    }).unwrap()
    globalToaster({ type: 'success', content: 'Record updated' })
   } else {
    await addRecord({ id: domainId, body: toRecordBody(record) }).unwrap()
    globalToaster({ type: 'success', content: 'Record added' })
   }
   setEditing(null)
  } catch (err) {
   // e.g. 422 "managed by AgentRoof": show the server's own message
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not save this record. Try again.'),
   })
  }
 }

 const onDelete = async (): Promise<void> => {
  if (deleting?.id == null) return
  try {
   await deleteRecord({ id: domainId, recordId: deleting.id }).unwrap()
   globalToaster({ type: 'success', content: 'Record deleted' })
   setDeleting(null)
  } catch (err) {
   globalToaster({
    type: 'error',
    content: getErrorMessage(err, 'Could not delete this record. Try again.'),
   })
  }
 }

 return (
  <div className="dns-records-section">
   <p className="sub">
    Records marked <LockTag /> keep your site and email working — they
    can&apos;t be edited here. Everything else is yours to manage.
   </p>

   {isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

   {isError && (
    <div className="info-box error">
     <i className="fas fa-exclamation-circle" />
     <span>
      {getErrorMessage(error, 'Could not load DNS records.')}{' '}
      <button type="button" className="link-btn" onClick={refetch}>
       Try again
      </button>
     </span>
    </div>
   )}

   {!isLoading && !isError && (
    <>
     {records?.length ? (
      <RecordsTable
       records={records}
       onEdit={(record) => setEditing({ record })}
       onDelete={(record) => setDeleting(record)}
      />
     ) : (
      <div className="records-empty">No records yet.</div>
     )}
     <button
      type="button"
      className="add-record-link"
      onClick={() => setEditing({ record: null })}
     >
      <i className="fas fa-plus" /> Add a record
     </button>
    </>
   )}

   <div className="info-box">
    <i className="fas fa-info-circle" />
    <span>
     Changes here apply immediately to your live domain. Only add or edit
     records you understand — an incorrect record can break email or other
     services.
    </span>
   </div>

   <RecordModal
    visible={!!editing}
    record={editing?.record}
    saving={isAdding || isUpdating}
    onCancel={() => setEditing(null)}
    onSave={onSave}
   />
   <DeleteRecordModal
    record={deleting}
    deleting={isDeleting}
    onCancel={() => setDeleting(null)}
    onConfirm={onDelete}
   />
  </div>
 )
}

export default DnsRecordsSection
